import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertCircle, Tag, Plus, Trash2, Phone, Mail, Building2 } from 'lucide-react';
import { Contact, ContactPhoneNumber, ContactEmail } from 'types';
import { contactsService } from 'api/services/contacts.service';
import { capturedFieldList } from 'utils/capturedFields';

interface ContactEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (contact: Contact) => void;
  editContact?: Contact | null;
  /**
   * Seed a new contact with a number already known — the caller on the line.
   * Ignored when `editContact` is set, since that is an edit, not a capture.
   */
  prefillPhone?: string;
}

const ContactEditModal: React.FC<ContactEditModalProps> = ({
  isOpen,
  onClose,
  editContact,
  onSuccess,
  prefillPhone,
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    notes: '',
    phone_numbers: [] as ContactPhoneNumber[],
    email_addresses: [] as ContactEmail[],
    labels: [] as string[],
  });

  const [newLabel, setNewLabel] = useState('');
  const [newPhone, setNewPhone] = useState({
    number: '',
    type: 'mobile' as const,
    whatsapp_enabled: false,
    rcs_enabled: false,
  });
  const [newEmail, setNewEmail] = useState({ email: '', type: 'work' as const });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editContact) {
      setFormData({
        first_name: editContact.first_name || '',
        last_name: editContact.last_name || '',
        company: editContact.company || '',
        notes: editContact.notes || '',
        phone_numbers: editContact.phone_numbers || [],
        email_addresses: editContact.email_addresses || [],
        labels: editContact.labels || [],
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        company: '',
        notes: '',
        // Seeding a number is NOT the same as editing an existing contact:
        // `editContact` stays undefined so submit still takes the create path.
        // Passing a half-built contact through `editContact` instead would send
        // an update keyed on an id that does not exist yet.
        phone_numbers: prefillPhone
          ? [{ number: prefillPhone, type: 'mobile', is_primary: true } as ContactPhoneNumber]
          : [],
        email_addresses: [],
        labels: [],
      });
    }
    setErrors({});
  }, [editContact, isOpen, prefillPhone]);

  // Only an existing contact can have captured data; a contact being created
  // has had no call yet. Memoised because this parses a JSON blob and the
  // component re-renders on every keystroke in every input above.
  const captured = useMemo(
    () => (editContact ? capturedFieldList(editContact) : []),
    [editContact]
  );

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim() && !formData.last_name.trim()) {
      newErrors.name = 'At least first name or last name is required';
    }

    if (formData.phone_numbers.length === 0 && formData.email_addresses.length === 0) {
      newErrors.general = 'At least one phone number or email address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Auto-add pending phone or email if user forgot to click "Add" button
    let finalFormData = { ...formData };

    if (newPhone.number.trim()) {
      const phoneNumber: ContactPhoneNumber = {
        number: newPhone.number.trim(),
        type: newPhone.type,
        is_primary: formData.phone_numbers.length === 0,
        verified: false,
        whatsapp_enabled: newPhone.whatsapp_enabled,
        rcs_enabled: newPhone.rcs_enabled,
      };
      finalFormData.phone_numbers = [...formData.phone_numbers, phoneNumber];
      setFormData(finalFormData);
    }

    if (newEmail.email.trim()) {
      const emailAddress: ContactEmail = {
        email: newEmail.email.trim(),
        type: newEmail.type,
        is_primary: formData.email_addresses.length === 0,
        verified: false,
      };
      finalFormData.email_addresses = [...finalFormData.email_addresses, emailAddress];
      setFormData(finalFormData);
    }

    // Validate with final data including auto-added phone/email
    const newErrors: Record<string, string> = {};

    if (!finalFormData.first_name.trim() && !finalFormData.last_name.trim()) {
      newErrors.name = 'At least first name or last name is required';
    }

    if (finalFormData.phone_numbers.length === 0 && finalFormData.email_addresses.length === 0) {
      newErrors.general = 'At least one phone number or email address is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);

    try {
      let result;

      if (editContact) {
        result = await contactsService.updateContact(editContact._uid, finalFormData);
      } else {
        result = await contactsService.createContact(finalFormData);
      }

      if (result.success && result.data) {
        onSuccess(result.data);
        handleClose();
      } else {
        setErrors({ general: result.error || 'Failed to save contact' });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      company: '',
      notes: '',
      phone_numbers: [],
      email_addresses: [],
      labels: [],
    });
    setErrors({});
    setNewLabel('');
    setNewPhone({ number: '', type: 'mobile', whatsapp_enabled: false, rcs_enabled: false });
    setNewEmail({ email: '', type: 'work' });
    onClose();
  };

  const addLabel = () => {
    if (newLabel.trim() && !formData.labels.includes(newLabel.trim())) {
      setFormData({
        ...formData,
        labels: [...formData.labels, newLabel.trim()],
      });
      setNewLabel('');
    }
  };

  const removeLabel = (label: string) => {
    setFormData({
      ...formData,
      labels: formData.labels.filter((l) => l !== label),
    });
  };

  const addPhone = () => {
    if (newPhone.number.trim()) {
      const phoneNumber: ContactPhoneNumber = {
        number: newPhone.number.trim(),
        type: newPhone.type,
        is_primary: formData.phone_numbers.length === 0,
        verified: false,
        whatsapp_enabled: newPhone.whatsapp_enabled,
        rcs_enabled: newPhone.rcs_enabled,
      };
      setFormData({
        ...formData,
        phone_numbers: [...formData.phone_numbers, phoneNumber],
      });
      setNewPhone({ number: '', type: 'mobile', whatsapp_enabled: false, rcs_enabled: false });
    }
  };

  const removePhone = (index: number) => {
    const updated = formData.phone_numbers.filter((_, i) => i !== index);
    // If we removed the primary phone, make the first one primary
    if (updated.length > 0 && !updated.some(p => p.is_primary)) {
      updated[0].is_primary = true;
    }
    setFormData({
      ...formData,
      phone_numbers: updated,
    });
  };

  const setPrimaryPhone = (index: number) => {
    const updated = formData.phone_numbers.map((p, i) => ({
      ...p,
      is_primary: i === index,
    }));
    setFormData({
      ...formData,
      phone_numbers: updated,
    });
  };

  const toggleWhatsApp = (index: number) => {
    const updated = [...formData.phone_numbers];
    updated[index] = {
      ...updated[index],
      whatsapp_enabled: !updated[index].whatsapp_enabled,
    };
    setFormData({
      ...formData,
      phone_numbers: updated,
    });
  };

  const toggleRCS = (index: number) => {
    const updated = [...formData.phone_numbers];
    updated[index] = {
      ...updated[index],
      rcs_enabled: !updated[index].rcs_enabled,
    };
    setFormData({
      ...formData,
      phone_numbers: updated,
    });
  };

  const addEmail = () => {
    if (newEmail.email.trim()) {
      const emailAddress: ContactEmail = {
        email: newEmail.email.trim(),
        type: newEmail.type,
        is_primary: formData.email_addresses.length === 0,
        verified: false,
      };
      setFormData({
        ...formData,
        email_addresses: [...formData.email_addresses, emailAddress],
      });
      setNewEmail({ email: '', type: 'work' });
    }
  };

  const removeEmail = (index: number) => {
    const updated = formData.email_addresses.filter((_, i) => i !== index);
    // If we removed the primary email, make the first one primary
    if (updated.length > 0 && !updated.some(e => e.is_primary)) {
      updated[0].is_primary = true;
    }
    setFormData({
      ...formData,
      email_addresses: updated,
    });
  };

  const setPrimaryEmail = (index: number) => {
    const updated = formData.email_addresses.map((e, i) => ({
      ...e,
      is_primary: i === index,
    }));
    setFormData({
      ...formData,
      email_addresses: updated,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {editContact ? 'Edit Contact' : 'Add Contact'}
          </h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{errors.general}</p>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>

              {errors.name && (
                <div className="col-span-2">
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                </div>
              )}

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Phone Numbers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Numbers
              </label>

              <div className="space-y-2 mb-3">
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={newPhone.number}
                    onChange={(e) => setNewPhone({ ...newPhone, number: e.target.value })}
                    placeholder="+1234567890"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <select
                    value={newPhone.type}
                    onChange={(e) => setNewPhone({ ...newPhone, type: e.target.value as any })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="mobile">Mobile</option>
                    <option value="work">Work</option>
                    <option value="home">Home</option>
                    <option value="other">Other</option>
                  </select>
                  <button
                    type="button"
                    onClick={addPhone}
                    className="tl-btn tl-btn-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>

                {/* Channel capabilities */}
                <div className="flex gap-4 pl-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPhone.whatsapp_enabled}
                      onChange={(e) => setNewPhone({ ...newPhone, whatsapp_enabled: e.target.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-green-600">WhatsApp Capable</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newPhone.rcs_enabled}
                      onChange={(e) => setNewPhone({ ...newPhone, rcs_enabled: e.target.checked })}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-purple-600">RCS Capable</span>
                  </label>
                </div>
              </div>

              {formData.phone_numbers.length > 0 && (
                <div className="space-y-3">
                  {formData.phone_numbers.map((phone, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="flex-1 text-sm font-medium text-gray-900">{phone.number}</span>
                        <span className="text-xs text-gray-500 capitalize">{phone.type}</span>
                        {phone.is_primary ? (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Primary</span>
                        ) : (
                          <button
                            onClick={() => setPrimaryPhone(index)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          onClick={() => removePhone(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Channel capabilities for existing numbers */}
                      <div className="flex gap-4 pl-6">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={phone.whatsapp_enabled || false}
                            onChange={() => toggleWhatsApp(index)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className={phone.whatsapp_enabled ? "text-green-600 font-medium" : "text-gray-600"}>
                            WhatsApp Capable
                          </span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={phone.rcs_enabled || false}
                            onChange={() => toggleRCS(index)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className={phone.rcs_enabled ? "text-purple-600 font-medium" : "text-gray-600"}>
                            RCS Capable
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email Addresses */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Addresses
              </label>

              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  value={newEmail.email}
                  onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
                  placeholder="email@example.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  value={newEmail.type}
                  onChange={(e) => setNewEmail({ ...newEmail, type: e.target.value as any })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="work">Work</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
                <button
                  type="button"
                  onClick={addEmail}
                  className="tl-btn tl-btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {formData.email_addresses.length > 0 && (
                <div className="space-y-2">
                  {formData.email_addresses.map((email, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="flex-1 text-sm text-gray-900">{email.email}</span>
                      <span className="text-xs text-gray-500 capitalize">{email.type}</span>
                      {email.is_primary ? (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Primary</span>
                      ) : (
                        <button
                          onClick={() => setPrimaryEmail(index)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Set Primary
                        </button>
                      )}
                      <button
                        onClick={() => removeEmail(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Labels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Tag className="w-4 h-4 inline mr-1" />
                Labels (Tags)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
                  placeholder="Add label..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addLabel}
                  className="tl-btn tl-btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {formData.labels.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.labels.map((label, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-sm flex items-center gap-2"
                    >
                      <Tag className="w-3 h-3" />
                      {label}
                      <button
                        type="button"
                        onClick={() => removeLabel(label)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this contact..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* What the voice agent captured on the call.
                Placed last and styled as a panel rather than as inputs,
                because it is evidence, not contact attributes: a record of
                what this caller said, which editing here could not change
                (the form submits its own fields and never writes this blob).
                Rendered only when there is something to show, so an ordinary
                contact grows no empty box. */}
            {captured.length > 0 && (
              <div>
                <h3 className="block text-sm font-medium text-gray-700 mb-1">
                  Captured on the call
                </h3>
                <dl className="rounded-lg border border-gray-200 bg-gray-50 divide-y divide-gray-200">
                  {captured.map((f) => (
                    <div key={f.key} className="sm:flex gap-3 px-3 py-2 text-sm">
                      <dt className="sm:w-52 shrink-0 text-gray-500">{f.label}</dt>
                      <dd className="min-w-0 text-gray-900 break-words whitespace-pre-wrap">
                        {f.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="text-[11px] mt-1 text-gray-400">
                  Recorded by the voice agent. Read-only.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : editContact ? 'Update Contact' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactEditModal;
